/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { ServicePopup } from './ServicePopup';
import type { TimeSeriesTypes } from '../../../store';
import { timeSeriesActions, timeSeriesSelectors } from '../../../store';
import type { TimeSeriesRootState } from '../../../store/types';

interface ServicePopupDialogConnectType {
  isOpen: boolean;
  panelId: string;
  serviceId?: string;
  variant: TimeSeriesTypes.PopupVariant;
  url?: string;
  setServicePopupClosed: () => void;
}

// Extracted from ServicePopupConnect as the getActiveServices and getActiveServiceById selectors
// Result in many rerenders. By extracting we're able to only render this component and these selectors
// when the dialog is open
export const ServicePopupDialogConnect: React.FC<
  ServicePopupDialogConnectType
> = ({
  isOpen,
  panelId,
  serviceId = '',
  variant,
  url = '',
  setServicePopupClosed,
}: ServicePopupDialogConnectType) => {
  const dispatch = useDispatch();

  const services = useSelector((store: TimeSeriesRootState) =>
    timeSeriesSelectors.getServices(store),
  );

  const activeService = useSelector((store: TimeSeriesRootState) =>
    timeSeriesSelectors.getService(store, serviceId!),
  );

  const popupState = useSelector((store: TimeSeriesRootState) =>
    timeSeriesSelectors.getServicePopupDetails(store, panelId),
  );

  const serviceSetService = React.useCallback(
    (payload: TimeSeriesService): void => {
      dispatch(
        timeSeriesActions.upsertService({
          panelId,
          ...payload,
          isUpdate: popupState.variant === 'edit',
        }),
      );
    },
    [dispatch, panelId, popupState],
  );

  const showSnackbar = React.useCallback(
    (message: string) => {
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message,
        }),
      );
    },
    [dispatch],
  );

  return (
    <ServicePopup
      closePopup={setServicePopupClosed}
      isOpen={isOpen}
      serviceDescription={activeService?.description}
      serviceName={activeService?.name}
      servicePopupVariant={variant}
      serviceSetService={serviceSetService}
      serviceUrl={url || activeService?.url}
      services={services}
      showSnackbar={showSnackbar}
    />
  );
};
